import React, {useReducer} from 'react';
import Models1Table from "./index";
import MultipleEditToolbar from "./MultipleEditToolbar";
import BulkUpdateDialog from "./BulkUpdateDialog";
import {useBulkUpdateModels1} from "../query";
import useHeadersState from "./useHeadersState";
import ExportToolbar from "./ExportToolbar";
import {useAuth} from "../../../auth/AuthProvider";


const EditableTableWrapper = (props) => {
    const {dataTableId} = props;
    const bulkUpdateModels1 = useBulkUpdateModels1();
    const {workgroupAuthorization} = useAuth();
    const [multipleEditing, toggleMultipleEditing] = useReducer((state) => !state, false);
    const [openBulkDialog, setOpenBulkDialog] = React.useState(false);

    const headersState = useHeadersState({rows: props.rows, isEditing: multipleEditing});

    const [model1State, dispatcher] = useReducer((state, action) => {
        switch (action.type) {
            case "update":
                const model1 = action.model1;
                return {
                    ...state,
                    updates: {...state.updates, [model1.id]: {...(state.updates[model1.id] ?? {}), ...model1}}
                };
            case "markForDelete":
                return {
                    ...state,
                    deletes: [...new Set([...state.deletes, action.id])]
                };
            case "unmarkForDelete":
                return {
                    ...state,
                    deletes: state.deletes.filter(id => id !== action.id)
                };
            case "bulkUpdate":
                const models1 = action.models1;
                return {
                    ...state,
                    updates: {
                        ...state.updates,
                        ...models1.reduce((acc, model1) => ({
                            ...acc,
                            [model1.id]: {...(state.updates[model1.id] ?? {}), ...model1}
                        }), {})
                    }
                };
            case "reset":
                return {updates: {}, deletes: []};
            default:
                return state;
        }
    }, {updates: {}, deletes: []});

    const {updates: model1Updates, deletes: model1Deletes} = model1State;

    const handleModel1Change = (model1) => {
        dispatcher({type: "update", model1: model1});
        Object.keys(model1).filter(key => key !== "id").forEach(key => headersState.resetHeader(key));
    }

    const handleModel1Delete = (id) => {
        if (model1Deletes.includes(id)) {
            dispatcher({type: "unmarkForDelete", id: id});
        } else {
            dispatcher({type: "markForDelete", id: id});
        }
    }

    const handleCancelUpdates = () => {
        dispatcher({type: "reset"});

        // this is important because changing the multipleEditing change cause the table to
        // re-render. without this the table will not re-render and the changes will still be
        // visible.
        toggleMultipleEditing();
    }

    const handleBulkUpdate = async () => {
        setOpenBulkDialog(true);
    }

    const handleBulkUpdateCompleted = () => {
        dispatcher({type: "reset"});
    }

    const handleHeaderChange = (table, columnId, value) => {
        const {rows} = table.getRowModel();
        const models1 = rows.map(row => ({id: row.original.id, [columnId]: value}));

        headersState.setHeader(columnId, value);
        dispatcher({type: "bulkUpdate", models1: models1});
    }

    return <>
        <Models1Table updates={model1Updates}
                      deletes={model1Deletes}
                      updateData={handleModel1Change}
                      deleteData={handleModel1Delete}
                      multipleEditing={multipleEditing}
                      headersState={headersState}
                      handleHeaderChange={handleHeaderChange}
                      dataTableId={dataTableId}
                      {...props}/>
        {
            workgroupAuthorization.models1.bulkUpdate &&
            <MultipleEditToolbar multipleEditing={multipleEditing}
                                 toggleMultipleEditing={toggleMultipleEditing}
                                 updatesCount={Object.keys(model1Updates).length}
                                 deletesCount={model1Deletes.length}
                                 onApply={handleBulkUpdate}
                                 onCancel={handleCancelUpdates}
                                 loading={bulkUpdateModels1.isLoading}/>
        }
        {openBulkDialog && <BulkUpdateDialog open={openBulkDialog}
                                             onClose={() => setOpenBulkDialog(false)}
                                             updates={model1Updates}
                                             deletes={model1Deletes}
                                             onCompleted={handleBulkUpdateCompleted}/>}
        <ExportToolbar models1Filters={props.exportFetchDataOptions}/>
    </>
}

export default EditableTableWrapper;